import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, CheckCircle, AlertCircle } from 'lucide-react';
import {
    collection, doc, documentId, getCountFromServer, getDocs, limit, orderBy,
    query, serverTimestamp, setDoc, startAfter,
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Reseller CRM — shared helpers (status config, utils, toast, status dropdown,
 * paginated hook, add-reseller function).
 * Firestore collection: `upcomming_reseller` (নামটা ইচ্ছাকৃতভাবেই এমন — আগের CRM এর সাথে মিল রাখা হয়েছে)
 */
export const RESELLER_COLLECTION = 'upcomming_reseller';

/**
 * দুটো CRM একই কোড ব্যবহার করে — শুধু config আলাদা।
 * Lead Store এর Firestore collection / ফিল্ডের নাম আলাদা হলে শুধু নিচের `store` config বদলান।
 *   collection : Firestore collection
 *   infoKey    : document এর ভেতরে মূল তথ্যের object (name, phone, email ...)
 *   orderField : list sort করার ফিল্ড (null হলে document id)
 */
export const CRM_CONFIGS = {
    reseller: {
        key: 'reseller',
        collection: RESELLER_COLLECTION,
        infoKey: 'get_reseller',
        orderField: 'reseller_id',
        title: 'Lead Reseller',
        singular: 'Reseller',
        plural: 'resellers',
        listPath: '/resellers',
        detailPath: '/reseller',
    },
    store: {
        key: 'store',
        collection: 'upcomming_store',
        infoKey: 'get_store',
        orderField: null,
        title: 'Lead Store',
        singular: 'Store',
        plural: 'stores',
        listPath: '/stores',
        detailPath: '/store',
    },
};

// document থেকে মূল তথ্য object বের করে (get_store না থাকলে get_reseller এ fallback)
export const getInfo = (cfg, docData) => docData?.[cfg.infoKey] || docData?.get_reseller || {};

// ─── Status config ────────────────────────────────────────────────────────────
// নতুন status চাইলে এখানে একটা entry বাড়ালেই হবে।
export const STATUS_OPTIONS = {
    new:            { label: 'New',            dot: '#94A3B8', className: 'bg-slate-100 text-slate-700 ring-slate-300' },
    follow_up:      { label: 'Follow Up',      dot: '#D97706', className: 'bg-amber-50 text-amber-800 ring-amber-300' },
    contacted:      { label: 'Contacted',      dot: '#2563EB', className: 'bg-blue-50 text-blue-700 ring-blue-300' },
    converted:      { label: 'Converted',      dot: '#187F60', className: 'bg-emerald-50 text-emerald-700 ring-emerald-300' },
    not_interested: { label: 'Not Interested', dot: '#DC2626', className: 'bg-red-50 text-red-700 ring-red-300' },
};
export const DEFAULT_STATUS = 'new';
export const getStatusMeta = (key) => STATUS_OPTIONS[key] || STATUS_OPTIONS[DEFAULT_STATUS];

// ─── Utils ────────────────────────────────────────────────────────────────────
// "01940546966" -> "8801940546966"
export function toWhatsAppNumber(phone) {
    if (!phone) return '';
    const digits = String(phone).replace(/\D/g, '');
    if (digits.startsWith('880')) return digits;
    if (digits.startsWith('0')) return `88${digits}`;
    return digits;
}

export function whatsAppLink(phone, message = '') {
    const number = toWhatsAppNumber(phone);
    const text = message ? `?text=${encodeURIComponent(message)}` : '';
    return `https://wa.me/${number}${text}`;
}

export const shopLink = (username) => (username ? `https://mohasagor.com.bd/shop/${username}` : '');

// custom website_link থাকলে সেটা, নাহলে username দিয়ে mohasagor shop link
export const getWebsiteLink = (info = {}) => info.website_link || shopLink(info.username);

export async function copyToClipboard(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
}

const toDate = (value) => (value?.toDate ? value.toDate() : new Date(value));

export function formatDateTime(value) {
    if (!value) return '—';
    const d = toDate(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatDate(value) {
    if (!value) return '—';
    const d = toDate(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export const initials = (name = '') =>
    name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');

// ─── Toast (বাকি page গুলোর মতো একই style) ─────────────────────────────────────
export function useToast() {
    const [toast, setToast] = useState(null);
    const timer = useRef(null);
    const showToast = useCallback((msg, type = 'success') => {
        setToast({ msg, type });
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setToast(null), 2800);
    }, []);
    useEffect(() => () => clearTimeout(timer.current), []);
    return { toast, showToast };
}

export const Toast = ({ toast }) => {
    if (!toast) return null;
    return (
        <div className={`fixed top-5 right-5 z-[9999] flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-md text-sm font-semibold border
            ${toast.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
            {toast.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
            {toast.msg}
        </div>
    );
};

// ─── Status dropdown ──────────────────────────────────────────────────────────
export function StatusDropdown({ status, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const meta = getStatusMeta(status);

    useEffect(() => {
        const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    return (
        <div className="relative" ref={ref}>
            <button type="button" onClick={() => setOpen((v) => !v)}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition hover:brightness-95 ${meta.className}`}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.dot }} aria-hidden="true" />
                {meta.label}
                <ChevronDown size={12} strokeWidth={2.5} />
            </button>
            {open && (
                <div className="absolute right-0 z-20 mt-1 w-40 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
                    {Object.entries(STATUS_OPTIONS).map(([key, opt]) => (
                        <button key={key} type="button"
                            onClick={() => { onChange(key); setOpen(false); }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-orange-50">
                            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: opt.dot }} aria-hidden="true" />
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Paginated resellers hook (cursor based, 20 per page) ─────────────────────
export const RESELLER_PAGE_SIZE = 20;

export function useResellers(cfg = CRM_CONFIGS.reseller) {
    const [cursors, setCursors] = useState([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasNext, setHasNext] = useState(false);
    const [totalCount, setTotalCount] = useState(null);

    const loadPage = useCallback(async (index, cursor) => {
        setLoading(true);
        setError(null);
        try {
            const constraints = [orderBy(cfg.orderField || documentId()), limit(RESELLER_PAGE_SIZE + 1)];
            if (cursor) constraints.splice(1, 0, startAfter(cursor));
            const snap = await getDocs(query(collection(db, cfg.collection), ...constraints));
            const docs = snap.docs.slice(0, RESELLER_PAGE_SIZE);
            setHasNext(snap.docs.length > RESELLER_PAGE_SIZE);
            setRows(docs.map((d) => ({ ...d.data(), id: d.id })));
            setCursors((prev) => {
                const next = [...prev];
                next[index] = docs[docs.length - 1] || null;
                return next;
            });
        } catch (e) {
            console.error('Reseller load failed:', e?.code, e?.message);
            setError(e);
        } finally {
            setLoading(false);
        }
    }, [cfg.collection, cfg.orderField]);

    const loadCount = useCallback(() => {
        getCountFromServer(collection(db, cfg.collection))
            .then((snap) => setTotalCount(snap.data().count))
            .catch(() => setTotalCount(null));
    }, [cfg.collection]);

    useEffect(() => {
        loadPage(0, null);
        loadCount();
    }, [loadPage, loadCount]);

    const goNext = () => {
        if (!hasNext) return;
        const next = pageIndex + 1;
        setPageIndex(next);
        loadPage(next, cursors[pageIndex]);
    };

    const goPrev = () => {
        if (pageIndex === 0) return;
        const prev = pageIndex - 1;
        setPageIndex(prev);
        loadPage(prev, prev === 0 ? null : cursors[prev - 1]);
    };

    const refresh = () => {
        setPageIndex(0);
        setCursors([]);
        loadPage(0, null);
        loadCount();
    };

    return { rows, loading, error, hasNext, hasPrev: pageIndex > 0, pageIndex, totalCount, goNext, goPrev, refresh, pageSize: RESELLER_PAGE_SIZE };
}

// ─── Manual reseller add ──────────────────────────────────────────────────────
// timestamp-based বড় numeric id — আসল subscription system এর reseller_id (15xxx) এর সাথে collide করবে না।
export async function addReseller(formData, cfg = CRM_CONFIGS.reseller) {
    const newId = Date.now();
    await setDoc(doc(db, cfg.collection, String(newId)), {
        id: newId,
        package_id: null,
        reseller_id: newId,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        renew_date: null,
        status: 1,
        crm_status: DEFAULT_STATUS,
        manual: true,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        get_package: formData.package_name
            ? { name: formData.package_name, price: formData.package_price || null }
            : null,
        [cfg.infoKey]: {
            name: formData.name,
            company_name: formData.company_name || null,
            username: formData.username || null,
            email: formData.email || null,
            phone: formData.phone || null,
            address: formData.address || null,
            facebook_link: formData.facebook_link || null,
            website_link: formData.website_link || null,
        },
        subscription_bills: null,
    });
    return newId;
}
